import {DurationType} from '@nursio-link/common';
import {
  calculateNextOccurrence,
  getDateFromIso,
  getTimeFromIso,
} from '@nursio-link/common/utils';
import dayjs from 'dayjs';
import {v4 as uuidv4} from 'uuid';

import type {
  CreateTaskInput,
  Duration,
  RecurringTask,
  Task,
} from '@/services/graphqlHooks';
import {PartOfTheDay} from '@/services/graphqlHooks';

export type ForecastedTask = Task & {
  isForecasted?: boolean;
};

const generateTasksToCreate = (
  recurringTask: RecurringTask,
  limitTimestamp: number
): CreateTaskInput[] => {
  const {
    id,
    lastOccurrenceTimestamp,
    interval,
    startTimestamp,
    content,
    title,
    occurrences,
    currentOccurrence,
    assignedToResident,
    assignedToEmployee,
    jobRoleIds,
    timeZone,
  } = recurringTask;

  const maxOccurrences = occurrences >= 0 ? occurrences : Infinity;
  const initialOccurrence =
    lastOccurrenceTimestamp ?? dayjs(startTimestamp).valueOf();

  return generateTasksRecursively(
    initialOccurrence,
    interval,
    maxOccurrences,
    currentOccurrence,
    limitTimestamp,
    {
      id,
      content,
      title,
      assignedToResident,
      assignedToEmployee,
      jobRoleIds,
      completeUntil: recurringTask.completeUntil,
      timeZone,
      startTimestamp,
    }
  );
};

type TaskDetails = Pick<
  RecurringTask,
  | 'content'
  | 'title'
  | 'assignedToResident'
  | 'assignedToEmployee'
  | 'jobRoleIds'
  | 'completeUntil'
  | 'startTimestamp'
  | 'timeZone'
> & {id: string};

const generateTasksRecursively = (
  lastOccurrence: number,
  interval: Duration,
  maxOccurrences: number,
  currentOccurrence: number,
  limitTimestamp: number,
  taskDetails: TaskDetails,
  tasks: CreateTaskInput[] = []
): CreateTaskInput[] => {
  const isInitialTask = currentOccurrence === 0 && tasks.length === 0;
  const nextOccurrenceTimestamp = isInitialTask
    ? lastOccurrence
    : calculateNextOccurrence(
        lastOccurrence,
        {...interval, type: DurationType[interval.type]},
        taskDetails
      );

  if (
    !isTimestampBeforeLimit(nextOccurrenceTimestamp, limitTimestamp) ||
    tasks.length + currentOccurrence >= maxOccurrences
  ) {
    return tasks;
  }

  const newTask: CreateTaskInput = {
    date: getDateFromIso(dayjs(nextOccurrenceTimestamp).toISOString()),
    content: taskDetails.content,
    title: taskDetails.title,
    assignedToResident: taskDetails.assignedToResident,
    assignedToEmployee: taskDetails.assignedToEmployee,
    jobRoleIds: taskDetails.jobRoleIds,
    completeUntil: {
      time: taskDetails.completeUntil?.time
        ? getTimeFromIso(dayjs(nextOccurrenceTimestamp).toISOString())
        : undefined,
      partOfTheDay: taskDetails.completeUntil?.time
        ? getPartOfTheDayFromTimestamp(nextOccurrenceTimestamp)
        : taskDetails.completeUntil?.partOfTheDay,
    },
    parentRecurringTaskId: taskDetails.id,
  };

  return generateTasksRecursively(
    nextOccurrenceTimestamp,
    interval,
    maxOccurrences,
    currentOccurrence,
    limitTimestamp,
    taskDetails,
    [...tasks, newTask]
  );
};

export const isTimestampBeforeLimit = (
  timestamp: number,
  limitTimestamp: number
): boolean => dayjs(timestamp).isBefore(limitTimestamp);

export const getPartOfTheDayFromTimestamp = (
  time: string | number
): PartOfTheDay => {
  const hour = new Date(time).getUTCHours();

  if (hour >= 18 || hour < 5) return PartOfTheDay.Night;
  if (hour >= 5 && hour < 10) return PartOfTheDay.Morning;
  if (hour >= 10 && hour < 14) return PartOfTheDay.Afternoon;
  if (hour >= 14 && hour < 18) return PartOfTheDay.Evening;

  throw new Error(`Invalid hour: ${hour} from timestamp: ${time}`);
};

export const getForecastedTasks = (
  recurringTasks: RecurringTask[]
): ForecastedTask[] => {
  const dateLimit = dayjs().add(7, 'days').unix() * 1000;

  return recurringTasks.flatMap(recurringTask => {
    const tasksToCreate = generateTasksToCreate(recurringTask, dateLimit);
    return tasksToCreate.map(task => ({
      ...task,
      createdBy: '',
      isForecasted: true,
      isLimitedToJobRoles: !!task.jobRoleIds?.length,
      createdAt: new Date().toISOString(),
      id: uuidv4(), // Ensures a unique ID for each task
      updatedAt: new Date().toISOString(),
    }));
  });
};
